const FAVICON_SIZE = 32;

/** Ярко-красный («вырви-глаз») фон круга — базовый кадр пульса. */
const BADGE_COLOR_BASE = '#ff0000';
/** Более яркий красный — второй кадр пульса (эффект мигания). */
const BADGE_COLOR_PULSE = '#ff3b30';
const BADGE_TEXT_COLOR = '#ffffff';

/** Радиус круга базового кадра и кадра-пульса (пульс чуть крупнее). */
const RADIUS_BASE = 13;
const RADIUS_PULSE = 15;

/** Период переключения кадров пульса, мс. */
const PULSE_INTERVAL_MS = 600;

/** Максимум, который влезает в круг; выше показываем «99+». */
const MAX_DISPLAY_COUNT = 99;

const FAVICON_REL = 'icon';

/** Текст счётчика для круга: число либо «99+» при переполнении. */
const formatCount = (count: number): string =>
  count > MAX_DISPLAY_COUNT ? `${MAX_DISPLAY_COUNT}+` : String(count);

/** Размер шрифта подбираем под длину строки, чтобы цифры влезли в круг. */
const getFontSize = (text: string): number => {
  if (text.length >= 3) {
    return 13;
  }
  if (text.length === 2) {
    return 16;
  }
  return 20;
};

/**
 * Строит SVG-разметку красного круга с белым счётчиком по центру.
 *
 * @param count - число непросмотренных заказов
 * @param color - цвет фона круга (для кадров пульса)
 * @param radius - радиус круга (для кадров пульса)
 * @returns строка SVG
 */
const buildBadgeSvg = (
  count: number,
  color: string,
  radius: number
): string => {
  const text = formatCount(count);
  const center = FAVICON_SIZE / 2;
  const fontSize = getFontSize(text);

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${FAVICON_SIZE}" height="${FAVICON_SIZE}" viewBox="0 0 ${FAVICON_SIZE} ${FAVICON_SIZE}"><circle cx="${center}" cy="${center}" r="${radius}" fill="${color}"/><text x="${center}" y="${center}" fill="${BADGE_TEXT_COLOR}" font-family="Arial, Helvetica, sans-serif" font-size="${fontSize}" font-weight="700" text-anchor="middle" dominant-baseline="central">${text}</text></svg>`;
};

/** Оборачивает SVG-разметку в data-URL для `href` favicon. */
const toDataUrl = (svg: string): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

/** Находит существующий `<link rel="icon">` в `<head>`. */
const findFaviconLink = (): HTMLLinkElement | null =>
  document.querySelector<HTMLLinkElement>('link[rel="icon"]');

let baseHref: string | null = null;

/** Запоминает исходный href favicon один раз, чтобы возвращаться к нему. */
const getBaseHref = (): string | null => {
  if (baseHref === null) {
    baseHref = findFaviconLink()?.href ?? null;
  }
  return baseHref;
};

const setFaviconHref = (href: string): void => {
  const existing = findFaviconLink();
  const link = existing ?? document.createElement('link');
  link.rel = FAVICON_REL;
  link.href = href;

  if (existing === null) {
    document.head.appendChild(link);
  }
};

let pulseTimerId: ReturnType<typeof setInterval> | null = null;
let pulseFrame = 0;
// Текущее число; интервал читает его, чтобы пульс показывал свежий счётчик
// без перезапуска таймера при каждом изменении count.
let currentCount = 0;

/** Останавливает таймер пульса (идемпотентно). */
const stopPulse = (): void => {
  if (pulseTimerId !== null) {
    clearInterval(pulseTimerId);
    pulseTimerId = null;
  }
  pulseFrame = 0;
};

/** Рисует текущий кадр пульса (чередуем базовый и увеличенный/яркий). */
const renderFrame = (): void => {
  const isPulse = pulseFrame % 2 === 1;
  const svg = buildBadgeSvg(
    currentCount,
    isPulse ? BADGE_COLOR_PULSE : BADGE_COLOR_BASE,
    isPulse ? RADIUS_PULSE : RADIUS_BASE
  );
  setFaviconHref(toDataUrl(svg));
};

/**
 * Подменяет favicon на ярко-красный пульсирующий круг со счётчиком (на время,
 * пока есть непросмотренные заказы). Исходный href запоминается, чтобы вернуться
 * к нему через `resetBadge`. Пульс реализован сменой двух кадров по таймеру —
 * растровый favicon не поддерживает CSS-анимацию.
 *
 * @param count - число непросмотренных заказов
 */
export const applyBadge = (count: number): void => {
  // Запоминаем исходный favicon до первой подмены.
  getBaseHref();

  currentCount = count;
  // Перезапускаем кадры с базового, чтобы счётчик обновился сразу.
  pulseFrame = 0;
  renderFrame();

  if (pulseTimerId === null) {
    pulseTimerId = setInterval(() => {
      pulseFrame += 1;
      renderFrame();
    }, PULSE_INTERVAL_MS);
  }
};

/** Останавливает пульс и возвращает favicon к исходному состоянию. */
export const resetBadge = (): void => {
  stopPulse();
  currentCount = 0;

  const base = getBaseHref();
  if (base !== null) {
    setFaviconHref(base);
  }
};
